import PropTypes from 'prop-types';
import moment from 'moment';
import {
    Box, Typography, Stack, IconButton, Button,
    Tooltip, alpha, Menu, MenuItem, Chip,
} from '@mui/material';
import { useState } from 'react';
import Iconify from 'src/components/Iconify';

const STATUS_CONFIG = {
    DRAFT:    { label: 'Draft',            color: '#637381', bgcolor: 'rgba(145,158,171,0.12)', icon: 'eva:edit-2-fill' },
    SENT:     { label: 'Sent',             color: '#078DEE', bgcolor: 'rgba(7,141,238,0.1)',    icon: 'eva:paper-plane-fill' },
    VIEWED:   { label: 'Viewed',           color: '#FFAB00', bgcolor: 'rgba(255,171,0,0.1)',    icon: 'eva:eye-fill' },
    ACCEPTED: { label: 'Accepted & Signed', color: '#00AB55', bgcolor: 'rgba(0,171,85,0.1)',   icon: 'eva:checkmark-circle-2-fill' },
    EXPIRED:  { label: 'Expired',          color: '#FF4842', bgcolor: 'rgba(255,72,66,0.1)',   icon: 'eva:clock-fill' },
    DECLINED: { label: 'Declined',         color: '#FF4842', bgcolor: 'rgba(255,72,66,0.1)',   icon: 'eva:close-circle-fill' },
};

function formatDate(dateStr) {
    if (!dateStr) return null;
    return moment(dateStr).format('D MMM YYYY');
}

function formatCurrency(val) {
    const n = parseFloat(val);
    if (Number.isNaN(n)) return null;
    return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function OptionsSummary({ options }) {
    if (!options || options.length === 0) return null;
    const summaries = options.map((opt) => {
        const total = opt.costItems?.reduce((s, item) => {
            const qty = parseFloat(item.qty) || 0;
            const unit = parseFloat(item.unitPrice) || 0;
            return s + qty * unit;
        }, 0);
        const price = total > 0 ? formatCurrency(total) : null;
        return price ? `${opt.name || `Option ${options.indexOf(opt) + 1}`} ${price}` : (opt.name || `Option ${options.indexOf(opt) + 1}`);
    });
    return (
        <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }}>
            {options.length} option{options.length > 1 ? 's' : ''}: {summaries.join(' / ')}
        </Typography>
    );
}

OptionsSummary.propTypes = {
    options: PropTypes.array,
};
OptionsSummary.defaultProps = { options: [] };

export default function TreatmentPlanCard({
    plan,
    onView,
    onResend,
    onCopyLink,
    onDelete,
    onEdit,
}) {
    const [menuAnchor, setMenuAnchor] = useState(null);

    const status = plan?.status || 'DRAFT';
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;

    const planName = plan?.name || 'Treatment Plan';
    const sentAt = plan?.sent_at ? formatDate(plan.sent_at) : null;
    const sentVia = plan?.sent_via;
    const acceptedAt = plan?.accepted_at ? formatDate(plan.accepted_at) : null;
    const expiresAt = plan?.expires_at ? formatDate(plan.expires_at) : null;
    const viewedAt = plan?.viewed_at ? formatDate(plan.viewed_at) : null;
    const selectedOption = plan?.selected_option_name;
    const selectedTotal = plan?.selected_option_total;
    const options = plan?.options || [];

    const isAccepted = status === 'ACCEPTED';
    const isExpired = status === 'EXPIRED';
    const isDraft = status === 'DRAFT';
    const canResend = !isExpired && !isDraft;
    const canCopyLink = !!plan?.link;

    const handleMenuOpen = (e) => setMenuAnchor(e.currentTarget);
    const handleMenuClose = () => setMenuAnchor(null);

    return (
        <Box
            sx={{
                borderRadius: '12px',
                border: `1px solid`,
                borderColor: isAccepted ? 'rgba(0,171,85,0.24)' : 'rgba(145,158,171,0.16)',
                bgcolor: isAccepted ? 'rgba(0,171,85,0.03)' : '#fff',
                p: 2,
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
            }}
        >
            {/* Top row: name + status badge */}
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Iconify
                        icon={cfg.icon}
                        width={16}
                        sx={{ color: cfg.color, flexShrink: 0 }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 700,
                            fontSize: '13px',
                            color: '#212B36',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {planName}
                    </Typography>
                </Stack>
                <Box
                    sx={{
                        flexShrink: 0,
                        ml: 1,
                        px: '8px',
                        py: '2px',
                        borderRadius: '6px',
                        bgcolor: cfg.bgcolor,
                    }}
                >
                    <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 700, color: cfg.color }}>
                        {cfg.label}
                    </Typography>
                </Box>
            </Stack>

            {/* Template origin badges */}
            {(plan?.form_template_name || plan?.pricing_template_name) && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, mb: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                    {plan.form_template_name && (
                        <Chip
                            size="small"
                            icon={<Iconify icon="eva:file-text-fill" width={11} />}
                            label={plan.form_template_name}
                            sx={{ height: 18, fontSize: '10px', bgcolor: 'rgba(0,171,85,0.06)', color: '#637381' }}
                        />
                    )}
                    {plan.pricing_template_name && (
                        <Chip
                            size="small"
                            icon={<Iconify icon="eva:pricetags-fill" width={11} />}
                            label={plan.pricing_template_name}
                            sx={{ height: 18, fontSize: '10px', bgcolor: 'rgba(7,141,238,0.06)', color: '#637381' }}
                        />
                    )}
                </Stack>
            )}

            {/* Meta lines */}
            <Stack spacing={0.4} sx={{ mb: 1.5 }}>
                {sentAt && (
                    <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                        Sent: {sentAt}{sentVia ? ` via ${sentVia}` : ''}
                    </Typography>
                )}
                {isDraft && !sentAt && (
                    <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.disabled' }}>
                        Not yet sent
                    </Typography>
                )}
                {viewedAt && status === 'VIEWED' && (
                    <Typography variant="caption" sx={{ fontSize: '11px', color: '#FFAB00' }}>
                        Viewed: {viewedAt}
                    </Typography>
                )}
                {isAccepted && acceptedAt && (
                    <Typography variant="caption" sx={{ fontSize: '11px', color: '#00AB55', fontWeight: 600 }}>
                        Accepted & Signed: {acceptedAt}
                    </Typography>
                )}
                {isAccepted && selectedOption && (
                    <Typography variant="caption" sx={{ fontSize: '11px', color: '#00AB55' }}>
                        Selected: {selectedOption}{selectedTotal ? ` — ${formatCurrency(selectedTotal)}` : ''}
                    </Typography>
                )}
                {options.length > 0 && !isAccepted && (
                    <OptionsSummary options={options} />
                )}
                {expiresAt && !isExpired && !isAccepted && (
                    <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.disabled' }}>
                        Expires: {expiresAt}
                    </Typography>
                )}
                {isExpired && (
                    <Typography variant="caption" sx={{ fontSize: '11px', color: '#FF4842' }}>
                        Expired{expiresAt ? `: ${expiresAt}` : ''}
                    </Typography>
                )}
            </Stack>

            {/* Action buttons */}
            <Stack direction="row" alignItems="center" spacing={0.75}>
                {isAccepted ? (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onView?.(plan)}
                        sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '7px',
                            py: '3px',
                            px: '10px',
                            borderColor: 'rgba(0,171,85,0.3)',
                            color: '#00AB55',
                            '&:hover': { borderColor: '#00AB55', bgcolor: alpha('#00AB55', 0.06) },
                        }}
                    >
                        View Submission
                    </Button>
                ) : (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => (isDraft ? onEdit?.(plan) : onView?.(plan))}
                        sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '7px',
                            py: '3px',
                            px: '10px',
                            borderColor: 'rgba(145,158,171,0.3)',
                            color: '#637381',
                            '&:hover': { borderColor: '#212B36', color: '#212B36', bgcolor: 'transparent' },
                        }}
                    >
                        {isDraft ? 'Edit' : 'View'}
                    </Button>
                )}

                {canResend && (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onResend?.(plan)}
                        sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '7px',
                            py: '3px',
                            px: '10px',
                            borderColor: 'rgba(145,158,171,0.3)',
                            color: '#637381',
                            '&:hover': { borderColor: '#212B36', color: '#212B36', bgcolor: 'transparent' },
                        }}
                    >
                        Resend
                    </Button>
                )}

                {canCopyLink && (
                    <Tooltip title="Copy link">
                        <IconButton
                            size="small"
                            onClick={() => onCopyLink?.(plan)}
                            sx={{
                                color: '#C4CDD5',
                                '&:hover': { color: '#00AB55', bgcolor: alpha('#00AB55', 0.08) },
                            }}
                        >
                            <Iconify icon="eva:copy-fill" width={15} />
                        </IconButton>
                    </Tooltip>
                )}

                <Tooltip title="More actions">
                    <IconButton
                        size="small"
                        onClick={handleMenuOpen}
                        sx={{
                            color: '#C4CDD5',
                            '&:hover': { color: '#637381', bgcolor: 'rgba(145,158,171,0.08)' },
                        }}
                    >
                        <Iconify icon="eva:more-horizontal-fill" width={16} />
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* More menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        borderRadius: '10px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        minWidth: 160,
                    },
                }}
            >
                {isDraft && (
                    <MenuItem
                        onClick={() => { handleMenuClose(); onEdit?.(plan); }}
                        sx={{ fontSize: '13px', gap: 1.5, py: '6px' }}
                    >
                        <Iconify icon="eva:edit-2-fill" width={16} sx={{ color: '#637381' }} />
                        Edit Plan
                    </MenuItem>
                )}
                {isAccepted && (
                    <MenuItem
                        onClick={() => { handleMenuClose(); window.open(plan?.pdf_url, '_blank', 'noopener noreferrer'); }}
                        sx={{ fontSize: '13px', gap: 1.5, py: '6px' }}
                        disabled={!plan?.pdf_url}
                    >
                        <Iconify icon="eva:download-fill" width={16} sx={{ color: '#637381' }} />
                        Download PDF
                    </MenuItem>
                )}
                {canCopyLink && (
                    <MenuItem
                        onClick={() => { handleMenuClose(); onCopyLink?.(plan); }}
                        sx={{ fontSize: '13px', gap: 1.5, py: '6px' }}
                    >
                        <Iconify icon="eva:copy-fill" width={16} sx={{ color: '#637381' }} />
                        Copy Link
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => { handleMenuClose(); onDelete?.(plan); }}
                    sx={{ fontSize: '13px', gap: 1.5, py: '6px', color: '#FF4842' }}
                >
                    <Iconify icon="eva:trash-2-fill" width={16} sx={{ color: '#FF4842' }} />
                    Delete
                </MenuItem>
            </Menu>
        </Box>
    );
}

TreatmentPlanCard.propTypes = {
    plan:        PropTypes.object.isRequired,
    onView:      PropTypes.func,
    onResend:    PropTypes.func,
    onCopyLink:  PropTypes.func,
    onDelete:    PropTypes.func,
    onEdit:      PropTypes.func,
};

TreatmentPlanCard.defaultProps = {
    onView:     undefined,
    onResend:   undefined,
    onCopyLink: undefined,
    onDelete:   undefined,
    onEdit:     undefined,
};
